import { GrayDeleteButton } from "@components/buttons/gray-delete";
import { RollBackButton } from "@components/buttons/rollback";
import {
  SHBox,
  SHButton,
  SHDataGrid,
  SHDataGridRef,
  SHStack,
} from "@components/design-systems";
import { DefaultDataState } from "@components/design-systems/sh-data-grid/constant";
import { EditSVG } from "@components/svgs";
import { useFeatureTemplate } from "@hooks/useFeatureTemplate";
import { useNotification } from "@hooks/useNotification";
import { useUserPermissions } from "@hooks/userUserPermission";
import { TopBar, TopBarContainer } from "@layouts/top-bar";
import { FeatureTemplateListDTO } from "@models/feature-template/entities/featureTemplate";
import { FeatureTemplateStatus } from "@models/feature-template/enums/status";
import { useTheme } from "@mui/material";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { FeatureTemplateUpgradeToModifyNotification } from "../components/notifications/modify-feature-template";
import { getPracticeOverviewNavigateRoute } from "../utils";
import { AnalysisFeatureTemplateConfigs as Configs } from "./configs";
import {
  getAnalysisFeatureTemplateTableColumnDefs,
  FeatureTemplateNameOfFactory as nameOf,
} from "./utils";
import { AnalysisConfigs } from "../configs";

export const AnalysisFeatureTemplateListView = () => {
  const { palette } = useTheme();
  const { notify, closeNotify } = useNotification();
  const navigate = useNavigate();
  const { user, isAdviserTrialGroup } = useUserPermissions();
  const { updateFeatureTemplateStatus } = useFeatureTemplate();

  const ref = useRef<SHDataGridRef | null>(null);

  const [updatingStatusIds, setUpdatingStatusIds] = useState<string[]>([]);
  const [gridState, setGridState] = useState<Partial<TableState>>({
    ...DefaultDataState,
    sorting: [{ id: nameOf("lastModifiedDate"), desc: true }],
    columnFilters: [
      {
        id: nameOf("status"),
        value: [FeatureTemplateStatus.Active],
      },
    ],
  });

  const handleOnEdit = () =>
    notify("Editing coming soon.", {
      variant: "error",
      close: true,
    });

  const handleOnChangeStatus = async (
    featureTemplateId?: string,
    status?: FeatureTemplateStatus,
  ) => {
    if (isAdviserTrialGroup) {
      notify(
        <FeatureTemplateUpgradeToModifyNotification
          onNavigate={() => {
            navigate(
              getPracticeOverviewNavigateRoute(
                user?.userMetadata?.adviser_firm_id,
              ),
            );
            closeNotify("update_status");
          }}
        />,
        {
          ...AnalysisConfigs.DefaultSnackbarOptions,
          key: "update_status",
        },
      );

      return;
    }

    if (!featureTemplateId || !status) return;
    setUpdatingStatusIds((ids) => [...ids, featureTemplateId]);
    await updateFeatureTemplateStatus(featureTemplateId, status);
    setUpdatingStatusIds((ids) => ids.filter((id) => id !== featureTemplateId));
    ref.current?.refreshOdata();
  };

  const renderItemActions = (itemData: FeatureTemplateListDTO) => {
    return (
      <SHStack direction="row" justifyContent={"flex-start"} spacing={0.5}>
        <SHButton
          sx={{ fontSize: 13 }}
          startIcon={<EditSVG color={palette.common.white} />}
          variant="contained"
          disabled={false}
          onClick={handleOnEdit}
        >
          Edit
        </SHButton>
        {itemData.status === FeatureTemplateStatus.Archived ? (
          <RollBackButton
            disabled={updatingStatusIds.includes(itemData.id ?? "")}
            isLoading={updatingStatusIds.includes(itemData.id ?? "")}
            onClick={async () =>
              await handleOnChangeStatus(
                itemData.id,
                FeatureTemplateStatus.Active,
              )
            }
          />
        ) : (
          <GrayDeleteButton
            disabled={updatingStatusIds.includes(itemData.id ?? "")}
            isLoading={updatingStatusIds.includes(itemData.id ?? "")}
            onClick={async () =>
              await handleOnChangeStatus(
                itemData.id,
                FeatureTemplateStatus.Archived,
              )
            }
          />
        )}
      </SHStack>
    );
  };

  const columns: ColumnDef<FeatureTemplateListDTO, any>[] = useMemo(() => {
    return getAnalysisFeatureTemplateTableColumnDefs({
      renderItemActions,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TopBarContainer>
        <TopBar title={"Platform analysis"} />
      </TopBarContainer>

      <SHBox sx={{ marginTop: "25px" }}>
        <SHDataGrid
          ref={ref}
          odata={{
            url: Configs.DefaultODataRoute,
            pickColumns: Configs.PickColumns,
            globalFilterColumns: Configs.GlobalFilterColumns,
          }}
          emptyMessage={"No feature template."}
          columns={columns}
          state={gridState}
          onChangeState={(state) => setGridState(state)}
          searchBoxProps={{
            placeholder: "Search name and owners",
          }}
        />
      </SHBox>
    </>
  );
};
